import ReactMDEditor, { MDEditorProps } from "@uiw/react-md-editor";
import { Guid } from "guid-typescript";
import React, { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { withPreservedLineBreaks } from "../../../shared/utilty/remark-preserve-line-breaks";
import { CommonProps } from "../common-props";
import "./style.css";

const backendUrl = KuikaAppManager.GetBackendUrl();

type MDViewerProps = MDEditorProps & {
  theme?: "light" | "dark";
};

interface MDViewerState {}

class MDViewer extends PureComponent<MDViewerProps & CommonProps, MDViewerState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kmdviewer_${this.uniqueKey}`);

  private resizeObserver: ResizeObserver | undefined;

  setTimeID = undefined as any;

  constructor(props: MDViewerProps) {
    super(props);
    this.state = {};
  }

  handleAnchors = () => {
    const node = ReactDOM.findDOMNode(this) as Element;
    const anchors = node.getElementsByTagName("a") as any;
    for (const element of anchors) {
      element.target = "_blank";
    }
  };

  handleSizeChange = () => {
    const node = ReactDOM.findDOMNode(this) as Element;
    const sourceNode: HTMLElement = node.getElementsByClassName("w-md-editor-preview")[0] as HTMLElement;
    const firstNode: HTMLElement = node.getElementsByClassName("w-md-editor-content")[0] as HTMLElement;
    const secondNode: HTMLElement = node.getElementsByClassName("w-md-editor-show-preview")[0] as HTMLElement;

    const { height } = this.props.style || {};
    if (!height) {
      const dynamicHeight = `${sourceNode.clientHeight}px`;
      firstNode.style.height = dynamicHeight;
      secondNode.style.height = dynamicHeight;
    }
  };

  observeSizeChange = () => {
    const { height } = this.props.style || {};
    if (height) return;

    const node = ReactDOM.findDOMNode(this) as Element;
    const sourceNode = node?.getElementsByClassName("w-md-editor-preview")[0] as HTMLElement | undefined;
    if (!sourceNode || typeof ResizeObserver === "undefined") return;

    this.resizeObserver = new ResizeObserver(() => {
      this.handleSizeChange();
    });
    this.resizeObserver.observe(sourceNode);
  };

  getValue = () => {
    if (this.props.value) {
      const _val = (this.props.value as any)?.value || this.props.value;

      if (typeof _val === "object" && _val !== null) {
        const formattedJson = JSON.stringify(_val, null, 2);
        return `\`\`\`json
${formattedJson}
\`\`\``;
      }

      const value = _val.replaceAll("{KUIKARESOURCES/}", `${backendUrl}/resource/runtime/byid?resourceId=`);

      if ((value.startsWith("{") && value.endsWith("}")) || (value.startsWith("[") && value.endsWith("]"))) {
        try {
          const parsedJson = JSON.parse(value);
          const formattedJson = JSON.stringify(parsedJson, null, 2);
          return `\`\`\`json
${formattedJson}
\`\`\``;
        } catch (e) {
          return `\`\`\`
${value}
\`\`\``;
        }
      }

      try {
        const parsedJson = JSON.parse(value);
        const formattedJson = JSON.stringify(parsedJson, null, 2);
        return `\`\`\`json
${formattedJson}
\`\`\``;
      } catch (e) {
        return value;
      }
    }
  };

  componentWillUnmount() {
    clearTimeout(this.setTimeID);
    this.resizeObserver?.disconnect();
    this.style.removeStyle();
  }

  componentDidUpdate(prevProps: MDViewerProps) {
    this.handleAnchors();

    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentDidMount() {
    this.setDynamicStyle();
    this.handleSizeChange();
    this.observeSizeChange();
    this.handleAnchors();
  }

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";
    const { textDecoration, paddingLeft, paddingTop, paddingRight, paddingBottom, width, height, maxHeight } =
      this.props.style;
    if (textDecoration) {
      result += `.${className.trim()} .wmde-markdown-color p {
          text-decoration: ${textDecoration} !important;
        }`;
    }
    if (paddingLeft || paddingTop || paddingRight || paddingBottom) {
      result += `.${className.trim()} {
        padding: ${`${paddingTop}px`} ${`${paddingRight}px`} ${`${paddingBottom}px`} ${`${paddingLeft}px`};
      }`;

      let paddingLeftX: any = paddingLeft ?? 0;

      let newWidth = this.getNumbersOfStyleProperties(width) - paddingLeftX;

      result += `.${className.trim()} .w-md-editor {
        padding: 0 !important;
        width: ${newWidth}px !important;
      }`;
    }

    result += `.${className.trim()} {
      background: ${this.props.style?.backgroundColor ?? "white"};
    }`;

    result += `.${className.trim()} .w-md-editor-show-preview .w-md-editor-preview {
      height: max-content;
    }`;

    result += `.${className.trim()} .w-md-editor-text-input::selection {
      color: white;
      -webkit-text-fill-color: white;
    }`;

    result += `.${className.trim()} .w-md-editor-text-input::-moz-selection {
      color: white;
      -webkit-text-fill-color: white;
    }`;

    result += `.${className.trim()} .w-md-editor-text-input::-webkit-selection {
      color: white;
      -webkit-text-fill-color: white;
    }`;

    if (!height) {
      result += `.${className.trim()} {
        min-height: 32px;
      }`;
    }

    if (height || maxHeight) {
      result += `.${className.trim()} {
        overflow-y: auto;
      }`;
      result += `.${className.trim()} .w-md-editor, .${className.trim()} .w-md-editor-content {
        ${height ? `height: ${height} !important;` : ""}
        ${maxHeight ? `max-height: ${maxHeight} !important;` : ""}
      }`;
    }

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getClassName = () => {
    let result = "";
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result} kmdviewer_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getStyle = () => {
    const styleObj: React.CSSProperties = {};
    if (this.props.style?.height) {
      styleObj.height = this.props.style?.height;
    }
    if (this.props.style?.minHeight) {
      styleObj.minHeight = this.props.style?.minHeight;
    }
    if (this.props.style?.maxHeight) {
      styleObj.maxHeight = this.props.style?.maxHeight;
    }
    if (this.props.style?.width) {
      styleObj.width = this.props.style?.width;
    }
    if (this.props.style?.minWidth) {
      styleObj.minWidth = this.props.style?.minWidth;
    }
    if (this.props.style?.maxWidth) {
      styleObj.maxWidth = this.props.style?.maxWidth;
    }
    return styleObj;
  };

  getNumbersOfStyleProperties = (val: any) => {
    let numb: any = val?.match(/\d/g);
    numb = numb?.join("");
    return numb;
  };

  render(): ReactNode {
    const { theme = "light" } = this.props;

    // The wrapper div carries the component's style/fill/border, so the caller's
    // `kuika-style-<id>` (which the generated state CSS targets) must land here, not
    // only on the inner editor. `getClassName()` returns only ` kmdviewer_<key>`, so
    // merge `this.props.className` onto the wrapper.
    const baseClassName: string = `kuika_mdViewer${this.getClassName()}`;
    const wrapperClassName: string =
      typeof this.props.className === "string" ? `${baseClassName} ${this.props.className}`.trim() : baseClassName;

    return (
      <>
        <div className={wrapperClassName} style={this.getStyle()} data-color-mode={theme}>
          <ReactMDEditor
            {...this.props}
            value={this.getValue()}
            hideToolbar={true}
            preview="preview"
            previewOptions={withPreservedLineBreaks(this.props.previewOptions)}
          />
        </div>
      </>
    );
  }
}

const mdViewer = withCommonEvents(MDViewer);
export { mdViewer as MDViewer };
